import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "axios";
import api from "@/services/api";
import { activityService } from "@/services/activityService";
import useBusinessStore from "./businessStore";
import type {
  AuthState,
  HomeView,
  LoginResponse,
  RefreshTokenResponse,
  User,
} from "@/types/auth.types";

// Re-export types for backward compatibility — existing imports like
// `import useAuthStore, { User, UserRole } from '@/stores/authStore'` keep working.
// Future code should import directly from '@/types/auth.types'.
export type {
  User,
  UserRole,
  AuthState,
  Subscription,
  Company,
} from "@/types/auth.types";

export {
  isTenantOwner,
  isBusinessAdmin,
  isStaff,
  isCashier,
  isAnyAdmin,
  canManageOrders,
  canEditCompletedOrders,
} from "@/types/auth.types";

/**
 * Refresh token endpoint URL — kept as a constant because the refresh call
 * MUST use raw axios (not the configured api instance) to avoid recursive
 * 401 handling. If the api instance's interceptor fired refreshAuthToken
 * which itself triggered a 401, we'd infinite-loop.
 */
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

/**
 * How long after a successful fetch (or login response) the user object
 * is considered "fresh." During this window, fetchUserProfile() returns
 * immediately without hitting /auth/me — unless { force: true }.
 *
 * Day 14 audit §2.7 frontend half:
 *   Login response already contains the full user object. The previous
 *   code path was: login → set(user) → AuthProvider mount effect →
 *   fetchUserProfile() → GET /auth/me → set(user again). That second
 *   round-trip cost ~200-400ms on every login for zero benefit.
 *
 *   30s is enough to cover the post-login bootstrap AND a rapid re-mount
 *   storm (when RouteGuards flicker), short enough that genuinely stale
 *   data refreshes promptly on the next deliberate fetch.
 */
const PROFILE_FRESH_WINDOW_MS = 30_000;

// ────────────────────────────────────────────────────────────────────
// Helper: normalize backend's user shape → frontend's User type
// (backend returns _id; frontend uses id)
// ────────────────────────────────────────────────────────────────────

function normalizeUser(raw: LoginResponse["user"]): User {
  return {
    id: raw._id,
    name: raw.name,
    email: raw.email,
    role: raw.role,
    companyId: raw.companyId,
    businessId: raw.businessId,
    company: raw.company,
    profileImage: raw.profileImage,
    createdAt: raw.createdAt || new Date().toISOString(),
    lastLogin: raw.lastLogin,
    homeView: homeViewOf(raw.homeView),
  };
}

/** Never trust the shape: unknown or half-set → pick each time. */
export function homeViewOf(h: { mode?: string; businessId?: string | null } | null | undefined): HomeView {
  if (h?.mode === "all") return { mode: "all", businessId: null };
  if (h?.mode === "business" && h.businessId) return { mode: "business", businessId: String(h.businessId) };
  return { mode: "pick", businessId: null };
}

/** Set at sign-in; the business list reads it once to open the owner's chosen start. */
export const JUST_SIGNED_IN = "eaze.justSignedIn";

// ────────────────────────────────────────────────────────────────────
// Module-level guard for fetchUserProfile.
//
// WHY: fetchUserProfile is called from multiple mount points — the
// AuthProvider bootstrap effect AND Settings.loadProfileData, and
// historically a re-render storm could re-enter it before the first call
// resolved. Each call flips isLoading, which made RouteGuards swap the
// whole route tree to <AuthLoadingState/> and back, unmounting/remounting
// every page and re-firing on-mount fetches (/auth/me, /activity,
// /notifications) ~7×/second. This was THE remount loop.
//
// A single in-flight promise makes re-entry a no-op: concurrent callers
// await the same request, and isLoading flips exactly once per real fetch.
// Kept at module scope (not in store state) so it never triggers a render.
// ────────────────────────────────────────────────────────────────────
let profileFetchInFlight: Promise<void> | null = null;

// One refresh at a time (many requests can expire together).
let refreshInFlight: Promise<boolean> | null = null;

/** The newest refresh token — another tab may have just saved a newer one. */
function latestRefreshToken(fallback: string | null): string | null {
  try {
    const raw = localStorage.getItem("auth-storage");
    const saved = raw ? (JSON.parse(raw)?.state?.refreshToken as string | null) : null;
    return saved || fallback;
  } catch {
    return fallback;
  }
}

// ────────────────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────────────────

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      // Day 14 audit §2.7 — epoch ms of the last successful user fetch
      // (login response counts as a fetch). Used by fetchUserProfile to
      // short-circuit when the user object is still fresh.
      lastProfileFetchAt: null,
      mfaPending: null,
      mfaSetupRequired: false,

      setUser: (user) => set({ user }),
      setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      setToken: (token) => {
        set({ token });
        if (token) {
          localStorage.setItem("auth-token", token);
        } else {
          localStorage.removeItem("auth-token");
        }
      },
      setRefreshToken: (refreshToken) => set({ refreshToken }),

      // ── LOGIN ──────────────────────────────────────────────────────
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<LoginResponse>("/auth/login", {
            email,
            password,
          });

          // 2-step sign-in: keep the pending token, show the code screen.
          if (response.data.mfaRequired && response.data.mfaToken) {
            set({ mfaPending: { mfaToken: response.data.mfaToken, email }, isLoading: false, error: null });
            return "mfa";
          }

          const { accessToken, refreshToken, user: rawUser } = response.data;
          const user = normalizeUser(rawUser);

          set({
            token: accessToken,
            refreshToken,
            user,
            isAuthenticated: true,
            isLoading: false,
            mfaPending: null,
            mfaSetupRequired: Boolean(response.data.mfaSetupRequired),
            // Stamp freshness: the login response IS a fresh user fetch.
            // Any fetchUserProfile() call within the next 30s skips the
            // /auth/me round-trip.
            lastProfileFetchAt: Date.now(),
          });

          localStorage.setItem("auth-token", accessToken);
          try {
            sessionStorage.setItem(JUST_SIGNED_IN, "1");
          } catch {
            /* private window — they start on the list */
          }
          // Business selection happens on /portfolio post-login.
          // (See prior note: user.businessId is an ObjectId but
          // businessMiddleware expects a slug, so we don't seed here.)

          if (user.role === "cashier") {
            try {
              await activityService.createActivity({
                action: "Login",
                description: `Cashier ${user.name} logged in`,
                category: "user",
                metadata: {
                  userId: user.id,
                  userEmail: user.email,
                  userRole: user.role,
                },
              });
            } catch (logErr) {
              console.warn("Login activity log failed:", logErr);
            }
          }

          return true;
        } catch (error) {
          console.error("Login error:", error);
          const message =
            (axios.isAxiosError(error) && error.response?.data?.message) ||
            (error instanceof Error ? error.message : "Failed to login");
          set({
            error: message,
            isLoading: false,
          });
          return false;
        }
      },

      // ── FINISH A SIGN-IN (phone code, …) ───────────────────────────
      completeSignIn: async (d, email = "") => {
        if (d.mfaRequired && d.mfaToken) {
          set({ mfaPending: { mfaToken: d.mfaToken, email }, isLoading: false, error: null });
          return "mfa";
        }
        if (!d.accessToken || !d.refreshToken) return false;
        localStorage.setItem("auth-token", d.accessToken);
        set({ token: d.accessToken, refreshToken: d.refreshToken, mfaPending: null, mfaSetupRequired: Boolean(d.mfaSetupRequired), isLoading: false, error: null });
        await get().fetchUserProfile({ force: true });
        try {
          sessionStorage.setItem(JUST_SIGNED_IN, "1");
        } catch {
          /* private window */
        }
        return true;
      },

      // ── 2-STEP SIGN-IN ─────────────────────────────────────────────
      verifyMfa: async ({ code, recoveryCode }) => {
        const pending = get().mfaPending;
        if (!pending) return { ok: false };
        set({ isLoading: true, error: null });
        try {
          const res = await api.post("/auth/mfa/verify", { mfaToken: pending.mfaToken, ...(recoveryCode ? { recoveryCode } : { code }) });
          const d = res.data.data as { accessToken: string; refreshToken: string; recoveryCodesLeft?: number | null };
          localStorage.setItem("auth-token", d.accessToken);
          set({ token: d.accessToken, refreshToken: d.refreshToken, mfaPending: null, mfaSetupRequired: false, isLoading: false });
          // Load the user with the new token.
          await get().fetchUserProfile({ force: true });
          try {
            sessionStorage.setItem(JUST_SIGNED_IN, "1");
          } catch {
            /* private window */
          }
          return { ok: true, recoveryCodesLeft: d.recoveryCodesLeft ?? null };
        } catch (error) {
          const data = axios.isAxiosError(error) ? (error.response?.data as { message?: string; code?: string } | undefined) : undefined;
          // The 5-minute window ran out: start again from the password.
          if (data?.code === "MFA_EXPIRED") set({ mfaPending: null });
          set({ error: data?.message || "That code didn't work.", isLoading: false });
          return { ok: false };
        }
      },
      cancelMfa: () => set({ mfaPending: null, error: null }),
      mfaSetupDone: (accessToken) => {
        if (accessToken) localStorage.setItem("auth-token", accessToken);
        set({ mfaSetupRequired: false, ...(accessToken ? { token: accessToken } : {}) });
      },

      // ── LOGOUT ─────────────────────────────────────────────────────
      logout: async () => {
        const { user, token } = get();
        // End this device's session on the server (best effort).
        if (token) {
          axios.post(`${API_BASE_URL}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }).catch(() => {});
        }

        if (user?.role === "cashier") {
          try {
            await activityService.createActivity({
              action: "Logout",
              description: `Cashier ${user.name} logged out`,
              category: "user",
              metadata: {
                userId: user.id,
                userEmail: user.email,
                userRole: user.role,
              },
            });
          } catch (error) {
            console.error("Failed to log logout activity:", error);
          }
        }

        localStorage.removeItem("auth-token");
        useBusinessStore.getState().clearBusinessState();
        // Clear any pending profile fetch guard so a fresh login re-fetches.
        profileFetchInFlight = null;
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          lastProfileFetchAt: null,
          mfaPending: null,
          mfaSetupRequired: false,
        });
      },

      // ── CREATE CASHIER ─────────────────────────────────────────────
      createCashier: async (cashierData, password) => {
        set({ isLoading: true, error: null });
        try {
          const { user } = get();
          if (!user?.companyId) {
            throw new Error("Authentication required");
          }

          const response = await api.post("/users", {
            ...cashierData,
            password,
            role: "cashier",
          });

          set({ isLoading: false });
          return response.data;
        } catch (error) {
          console.error("Create cashier error:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to create cashier",
            isLoading: false,
          });
        }
      },

      // ── PASSWORD RESET (request) ───────────────────────────────────
      resetPassword: async (email) => {
        set({ isLoading: true, error: null });
        try {
          await api.post("/auth/reset-password", { email });
          set({ isLoading: false });
        } catch (error) {
          console.error("Password reset error:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to reset password",
            isLoading: false,
          });
        }
      },

      // ── PASSWORD RESET (confirm) ───────────────────────────────────
      confirmResetPassword: async (token, password) => {
        set({ isLoading: true, error: null });
        try {
          await api.post("/auth/reset-password-confirm", { token, password });
          set({ isLoading: false });
        } catch (error) {
          console.error("Password reset confirmation error:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to confirm password reset",
            isLoading: false,
          });
        }
      },

      // ── PROFILE UPDATE ─────────────────────────────────────────────
      updateProfile: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const { user } = get();
          if (!user) {
            throw new Error("Authentication required");
          }

          const response = await api.put<Partial<User>>("/auth/me", userData);

          set({
            user: { ...user, ...response.data },
            isLoading: false,
            // Profile just updated — bump freshness so any immediate
            // fetchUserProfile() skips re-fetching the same data.
            lastProfileFetchAt: Date.now(),
          });
        } catch (error) {
          console.error("Profile update error:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to update profile",
            isLoading: false,
          });
        }
      },

      // ── FETCH USER PROFILE ─────────────────────────────────────────
      // Guarded against re-entry AND against unnecessary refetches.
      //
      // Three-tier guard:
      //   1. In-flight: another fetch is running → await its promise.
      //   2. Freshness: lastProfileFetchAt is within PROFILE_FRESH_WINDOW_MS
      //      AND we already have a user → skip the request entirely.
      //      Caller gets resolved promise with the existing user.
      //   3. Otherwise: real fetch, sets isLoading.
      //
      // To force a refresh (e.g. after server-side changes that may not
      // round-trip through updateProfile), call with { force: true }.
      fetchUserProfile: async (options?: { force?: boolean }) => {
        const force = options?.force === true;

        // Tier 1: in-flight de-dupe
        if (profileFetchInFlight) {
          return profileFetchInFlight;
        }

        // Tier 2: freshness short-circuit
        if (!force) {
          const { user, lastProfileFetchAt } = get();
          if (
            user &&
            lastProfileFetchAt &&
            Date.now() - lastProfileFetchAt < PROFILE_FRESH_WINDOW_MS
          ) {
            // User is fresh enough — no network call, no isLoading flip.
            return Promise.resolve();
          }
        }

        const run = (async () => {
          set({ isLoading: true, error: null });
          try {
            const response = await api.get<LoginResponse["user"]>("/auth/me");
            const user = normalizeUser(response.data);

            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              lastProfileFetchAt: Date.now(),
            });
            // No business store re-sync here — see login() for why.
          } catch (error) {
            console.error("Fetch profile error:", error);
            set({
              error:
                error instanceof Error
                  ? error.message
                  : "Failed to fetch profile",
              isLoading: false,
            });

            // 401 means our token is gone or invalid — clear auth state
            if (axios.isAxiosError(error) && error.response?.status === 401) {
              set({
                user: null,
                token: null,
                refreshToken: null,
                isAuthenticated: false,
                lastProfileFetchAt: null,
              });
              useBusinessStore.getState().clearBusinessState();
            }
          } finally {
            profileFetchInFlight = null;
          }
        })();

        profileFetchInFlight = run;
        return run;
      },

      // ── REFRESH AUTH TOKEN ─────────────────────────────────────────
      // CRITICAL: uses raw axios (not the api instance) to avoid recursive
      // 401 handling.
      refreshAuthToken: async () => {
        if (refreshInFlight) return refreshInFlight;
        const run = (async () => {
          const refreshToken = latestRefreshToken(get().refreshToken);
          if (!refreshToken) return false;
          try {
            const response = await axios.post<RefreshTokenResponse>(`${API_BASE_URL}/auth/refresh-token`, { refreshToken }, { timeout: 15000 });
            const { accessToken, refreshToken: newRefreshToken } = response.data;
            // null = another tab already rotated it; keep the newest saved one.
            set({ token: accessToken, refreshToken: newRefreshToken || latestRefreshToken(refreshToken) });
            localStorage.setItem("auth-token", accessToken);
            return true;
          } catch (error) {
            // No signal isn't a sign-out: only a refusal from the server is.
            if (axios.isAxiosError(error) && error.response && [400, 401, 403].includes(error.response.status)) {
              set({ user: null, token: null, refreshToken: null, isAuthenticated: false, lastProfileFetchAt: null, mfaSetupRequired: false });
              useBusinessStore.getState().clearBusinessState();
            }
            return false;
          } finally {
            refreshInFlight = null;
          }
        })();
        refreshInFlight = run;
        return run;
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        mfaSetupRequired: state.mfaSetupRequired,
        // lastProfileFetchAt is NOT persisted — on page refresh we WANT
        // a fresh fetch (the user might have logged out elsewhere or the
        // token might have been revoked server-side). Treat each tab
        // bootstrap as cold.
      }),
    },
  ),
);

export default useAuthStore;
